import { useTranslation } from '../../i18n';
import React, { useState, useEffect } from 'react';
import CountryFlag, { countryFromRegion } from '../../components/CountryFlag';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    message,
    Progress,
    Row,
    Col,
    Card,
    Statistic,
    Descriptions,
    Tooltip,
    Empty
} from 'antd';
import {
    PlusOutlined,
    EyeOutlined,
    ReloadOutlined,
    DatabaseOutlined,
    GlobalOutlined,
    FolderOutlined,
    SafetyCertificateOutlined,
    SettingOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import hostSharedService from '../../../shared/services/hostSharedService';

import { userPath } from '../../../shared/config/paths';
import PageHeader from '../../components/PageHeader';
import { rowLink, ROW_LINK_CLASS } from '../../utils/rowLink';
import RowActions, { useRowContextMenu } from '../../components/RowActions';
import RenewAction from '../../components/RenewAction';
import { normalizeVhost, usagePercent } from './vhostView';

const MyVhosts = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [vhosts, setVhosts] = useState([]);
    const [pending, setPending] = useState([]); // 已付款、开通中（资源不足排队）
    const [loading, setLoading] = useState(false);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [currentDetail, setCurrentDetail] = useState(null);

    useEffect(() => {
        loadVhosts();
    }, []);

    const handleDelete = async (record) => {
        const res = await hostSharedService.deleteVhost(record.id);
        if (res?.success) {
            message.success(res.data?.purge_at
                ? t('Stopped. Files and database will be deleted on {{date}}.', { date: res.data.purge_at })
                : t('Deleted'));
            loadVhosts();
        } else {
            message.error(res?.error || t('Deletion failed'));
        }
    };

    const loadVhosts = async () => {
        setLoading(true);
        try {
            const [response, p] = await Promise.all([hostSharedService.getVhosts(), hostSharedService.getPending()]);
            setPending(p?.success && Array.isArray(p.data)
                ? p.data.map((x) => ({ ...x, id: `p${x.subscription_id}`, pending: true, name: x.domain })) : []);
            if (response.success) {
                setVhosts((Array.isArray(response.data) ? response.data : []).map(normalizeVhost));
            } else {
                message.error(response.error || t('Failed to retrieve hosting accounts'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve hosting accounts'));
        } finally {
            setLoading(false);
        }
    };

    // 创建走统一购买向导:选地区→选套餐→加购→下单,不再本页填表直建/直选节点。
    const handleCreate = () => {
        navigate(userPath('/provision/host_shared'));
    };

    const handleViewDetail = async (record) => {
        try {
            const response = await hostSharedService.getVhost(record.id);
            if (response.success) {
                setCurrentDetail(normalizeVhost(response.data));
                setDetailModalVisible(true);
            } else {
                message.error(response.error || t('Failed to retrieve hosting details'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve hosting details'));
        }
    };

    const handleManage = (record) => {
        // 跳转到虚拟主机详情页面
        navigate(userPath(`/host_shared/vhosts/${record.id}`));
    };

    const getProgressStatus = (percent) => {
        if (percent >= 90) return 'exception';
        if (percent >= 70) return 'normal';
        return 'success';
    };

    const getRowActions = (record) => (record.pending ? [] : [
        { key: 'manage', label: t('Manage'), icon: <SettingOutlined />, inline: true, primary: true, onClick: () => handleManage(record) },
        { key: 'detail', label: t('Details'), icon: <EyeOutlined />, inline: true, onClick: () => handleViewDetail(record) },
        { key: 'delete', label: t('Delete'), icon: <DeleteOutlined />, danger: true, confirm: { title: t('The site is stopped now and its files and database are permanently deleted after 7 days.'), okType: 'danger' }, onClick: () => handleDelete(record) },
    ]);
    const rowMenu = useRowContextMenu(getRowActions);

    const columns = [
        {
            title: t('Hostname'),
            dataIndex: 'name',
            key: 'name',
            render: (name, record) => (
                <Space direction="vertical" size="small">
                    <strong>{name}</strong>
                    <span style={{ fontSize: '12px', color: '#999' }}>
                        {record.pending ? record.product : record.domain}
                    </span>
                </Space>
            ),
        },
        {
            title: t('Node'),
            dataIndex: ['node', 'name'],
            key: 'node',
            render: (name, record) => {
                if (record.pending) return '-';
                const nodeName = name || t('Node #{{node_id}}', { node_id: record.node_id });
                const region = record.node?.region;
                return (
                    <Space direction="vertical" size="small">
                        <span>{nodeName}</span>
                        {region && (
                            <Tag color="blue" style={{ fontSize: '11px' }}><CountryFlag code={countryFromRegion(region)} />{region}</Tag>
                        )}
                    </Space>
                );
            },
        },
        {
            title: t('Disk usage'),
            dataIndex: 'disk_usage',
            key: 'disk_usage',
            width: 150,
            render: (used, record) => {
                if (record.pending) return '-';
                const quota = record.disk_quota || 0;
                const percent = usagePercent(used, quota);
                return (
                    <div>
                        <div style={{ fontSize: '12px', marginBottom: 4 }}>
                            {used || 0}MB / {quota}MB
                        </div>
                        <Progress
                            percent={percent}
                            status={getProgressStatus(percent)}
                            size="small"
                        />
                    </div>
                );
            },
        },
        {
            title: t('Bandwidth usage'),
            dataIndex: 'bandwidth_usage',
            key: 'bandwidth_usage',
            width: 150,
            render: (used, record) => {
                if (record.pending) return '-';
                const quota = record.bandwidth_quota || 0;
                const percent = usagePercent(used, quota);
                return (
                    <div>
                        <div style={{ fontSize: '12px', marginBottom: 4 }}>
                            {used || 0}GB / {quota}GB
                        </div>
                        <Progress
                            percent={percent}
                            status={getProgressStatus(percent)}
                            size="small"
                        />
                    </div>
                );
            },
        },
        {
            title: t('Resource statistics'),
            key: 'stats',
            width: 150,
            render: (_, record) => (record.pending ? '-' :
                <Space direction="vertical" size="small">
                    <span style={{ fontSize: '12px' }}>
                        <GlobalOutlined />{t(" Domain: {{value}}/{{value2}}", { value: record.domains_count || 0, value2: record.domain_quota === null || record.domain_quota < 0 ? '∞' : record.domain_quota })}
                    </span>
                    <span style={{ fontSize: '12px' }}>
                        <DatabaseOutlined /> {t('Database')}: {record.databases_count || 0}
                    </span>
                </Space>
            ),
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status, record) => (record.pending ? <Tag color="processing">{t('Activating')}</Tag> :
                <Tag color={record.active ? 'success' : 'error'}>
                    {record.active ? t('Normal') : t('Suspended')}
                </Tag>
            ),
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-',
        },
        {
            title: t('Expires'),
            key: 'billing',
            width: 170,
            render: (_, record) => (record.pending ? '-' : <RenewAction billing={record.billing} resourceName={record.domain || record.name} />),
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            render: (_, record) => (record.pending ? null : <RowActions actions={getRowActions(record)} />),
        },
    ];

    return (
        <div>
            <PageHeader
                title={t('Shared hosting')}
                subtitle={t('Shared hosting for websites')}
                extra={<Space>
                    <Button icon={<ReloadOutlined />} onClick={loadVhosts}>
                        {t('Refresh')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleCreate}
                    >
                        {t('Create virtual host')}
                    </Button>
                </Space>}
            />

            <Table
                    onRow={rowMenu.onRow(rowLink(navigate, (r) => (r.pending ? null : `/host_shared/vhosts/${r.id}`)))}
                    rowClassName={(r) => (r.pending ? '' : ROW_LINK_CLASS)}
                columns={columns}
                dataSource={[...pending, ...vhosts]}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={t('You have not created any shared hosting accounts yet')}
                        >
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                            >
                                {t('Create your first virtual host')}
                            </Button>
                        </Empty>
                    )
                }}
                pagination={{
                    showSizeChanger: true,
                    showTotal: (total) => t('{{total}} shared hosts total', { total: total }),
                }}
            />
            {rowMenu.menu}

            {/* 虚拟主机详情模态框 */}
            <Modal
                title={t('Hosting details')}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setDetailModalVisible(false)}>
                        {t('Close')}
                    </Button>,
                    currentDetail && (
                        <Button
                            key="manage"
                            type="primary"
                            onClick={() => {
                                setDetailModalVisible(false);
                                handleManage(currentDetail);
                            }}
                        >
                            {t('Manage')}
                        </Button>
                    )
                ]}
                width={800}
            >
                {currentDetail && (
                    <>
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title={t('Disk utilization')}
                                        value={currentDetail.disk_quota > 0 ? Math.round((currentDetail.disk_usage / currentDetail.disk_quota) * 100) : 0}
                                        suffix="%"
                                        valueStyle={{
                                            color: currentDetail.disk_quota > 0 && (currentDetail.disk_usage / currentDetail.disk_quota) >= 0.9 ? '#cf1322' : '#3f8600'
                                        }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                        {currentDetail.disk_usage || 0}MB / {currentDetail.disk_quota}MB
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title={t('Bandwidth utilization')}
                                        value={currentDetail.bandwidth_quota > 0 ? Math.round((currentDetail.bandwidth_usage / currentDetail.bandwidth_quota) * 100) : 0}
                                        suffix="%"
                                        valueStyle={{
                                            color: currentDetail.bandwidth_quota > 0 && (currentDetail.bandwidth_usage / currentDetail.bandwidth_quota) >= 0.9 ? '#cf1322' : '#3f8600'
                                        }}
                                    />
                                    <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                        {currentDetail.bandwidth_usage || 0}GB / {currentDetail.bandwidth_quota}GB
                                    </div>
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title={t('Domain count')}
                                        value={currentDetail.domains_count || 0}
                                        suffix={`/ ${currentDetail.domain_quota === null || currentDetail.domain_quota < 0 ? '∞' : currentDetail.domain_quota}`}
                                    />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card>
                                    <Statistic
                                        title={t('Database count')}
                                        value={currentDetail.databases_count || 0}
                                    />
                                </Card>
                            </Col>
                        </Row>

                        <Descriptions bordered column={2}>
                            <Descriptions.Item label={t('Hostname')} span={1}>{currentDetail.name}</Descriptions.Item>
                            <Descriptions.Item label={t('Hosting ID')} span={1}>{currentDetail.id}</Descriptions.Item>
                            <Descriptions.Item label={t('Primary domain')} span={2}>{currentDetail.domain}</Descriptions.Item>
                            <Descriptions.Item label={t('Node')} span={1}>
                                {currentDetail.node?.name || t('Node #{{node_id}}', { node_id: currentDetail.node_id })}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Zone')} span={1}>
                                <CountryFlag code={countryFromRegion(currentDetail.node?.region)} />{currentDetail.node?.region || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('FTP quota')} span={1}>
                                {t("{{value}} accounts", { value: currentDetail.ftp_quota || 0 })}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Status')} span={1}>
                                <Tag color={currentDetail.active ? 'success' : 'error'}>
                                    {currentDetail.active ? t('Running normally') : t('Paused')}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Created at')} span={1}>
                                {currentDetail.created_at ? new Date(currentDetail.created_at).toLocaleString('zh-CN') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Updated at')} span={1}>
                                {currentDetail.updated_at ? new Date(currentDetail.updated_at).toLocaleString('zh-CN') : '-'}
                            </Descriptions.Item>
                            {currentDetail.remark && (
                                <Descriptions.Item label={t('Notes')} span={2}>
                                    {currentDetail.remark}
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default MyVhosts;
